// src/application-steps/StepContent4.js

import React, { useEffect, useState, useContext, memo } from "react";

import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import FormInput from "../../helpers/FormInput";
import DeleteIcon from "@mui/icons-material/Delete";

import {
  useGetDasStep4Query,
  useAddDasStep4Mutation,
  useDeleteDasStep4Mutation,
} from "../../../services/das-app";
import { NotificationContext } from "../../../context/NotificationContext";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  borderRadius: "8px",
  boxShadow: 24,
  p: 4,
};

function StepContent4({
  formData,
  setFormData,
  stepErrors,
  handleChange,
  isReadOnly,
  appID,
}) {
  const { addNotification } = useContext(NotificationContext);
  const { data, isLoading, isError, refetch } = useGetDasStep4Query(appID, {
    skip: !appID,
  });
  const [addDasStep4] = useAddDasStep4Mutation();
  const [deleteDasStep4] = useDeleteDasStep4Mutation();

  const [openModal, setOpenModal] = useState(false);
  const [currentType, setCurrentType] = useState("");
  const [newEntry, setNewEntry] = useState({
    Data1: "",
    Data2: "",
  });

  useEffect(() => {
    if (data && data.success && data.data) {
      // If needed, process data here
      // Currently, data is managed via the query and displayed directly
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleOpenModal = (type) => {
    setCurrentType(type);
    setNewEntry({ Data1: "", Data2: "" });
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setCurrentType("");
    setNewEntry({ Data1: "", Data2: "" });
  };

  const handleOldChange = (field) => (e) => {
    setNewEntry((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleSaveEntry = async () => {
    if (newEntry.Data1.trim() === "" || newEntry.Data2.trim() === "") {
      alert("All fields are required.");
      return;
    }

    try {
      await addDasStep4({
        appID,
        data: {
          Type: currentType,
          Data1: newEntry.Data1.trim(),
          Data2: newEntry.Data2.trim(),
        },
      }).unwrap();
      addNotification("Entry added successfully.", "success");
      handleCloseModal();
      refetch();
    } catch (error) {
      console.error("Error adding entry:", error);
      alert("Failed to add entry. Please try again.");
    }
  };

  const handleDeleteEntry = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this entry?"
    );
    if (!confirmDelete) return;

    try {
      await deleteDasStep4({ appID, id }).unwrap();
      addNotification("Entry deleted successfully.", "success");
      refetch();
    } catch (error) {
      console.error("Error deleting entry:", error);
      alert("Failed to delete entry. Please try again.");
    }
  };

  const renderTable = (type) => {
    const filteredData =
      data?.data.filter((entry) => entry.Type === type) || [];

    return (
      <TableContainer component={Paper} sx={{ marginBottom: 4 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>
                  {type === "Course"
                    ? "Course Name"
                    : type === "Extracurricular"
                    ? "Activity"
                    : "Award Name"}
                </strong>
              </TableCell>
              <TableCell>
                <strong>
                  {type === "Course"
                    ? "Teacher/Sponsor"
                    : type === "Extracurricular"
                    ? "Teacher/Sponsor"
                    : "Organization"}
                </strong>
              </TableCell>
              <TableCell align="right">
                <strong>Actions</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData?.map((entry) => (
              <TableRow key={entry?.id}>
                <TableCell>{entry?.Data1}</TableCell>
                <TableCell>{entry?.Data2}</TableCell>
                <TableCell align="right">
                  <IconButton
                    aria-label="delete"
                    color="error"
                    onClick={() => handleDeleteEntry(entry?.id)}
                    disabled={isReadOnly}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {filteredData?.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  No entries found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <div className="form-group">
      <h3 className="bold-text">Courses</h3>
      <Typography variant="body1" gutterBottom>
        List courses that you have taken related to your area of interest
      </Typography>
      {isLoading ? (
        <Typography>Loading...</Typography>
      ) : isError ? (
        <Typography color="error">Failed to load courses.</Typography>
      ) : (
        <>
          {renderTable("Course")}
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ mt: 1, mb: 1 }}
          >
            * Please ensure at least one course is added.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleOpenModal("Course")}
            disabled={isReadOnly}
          >
            Add Course
          </Button>
        </>
      )}
      <h3 className="bold-text">Extracurricular Activities</h3>

      <Typography variant="body1" gutterBottom>
        List activities that are related to your area of interest
      </Typography>
      {isLoading ? (
        <Typography>Loading...</Typography>
      ) : isError ? (
        <Typography color="error">
          Failed to load extracurricular activities.
        </Typography>
      ) : (
        <>
          {renderTable("Extracurricular")}
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleOpenModal("Extracurricular")}
            disabled={isReadOnly}
          >
            Add Extracurricular Activity
          </Button>
        </>
      )}

      <h3 className="bold-text">Awards</h3>

      <Typography variant="body1" gutterBottom>
        In this section list any awards, certificates or competitions connected
        to your area of interest over the last two years.
      </Typography>
      {isLoading ? (
        <Typography>Loading...</Typography>
      ) : isError ? (
        <Typography color="error">Failed to load awards.</Typography>
      ) : (
        <>
          {renderTable("Award")}
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleOpenModal("Award")}
            disabled={isReadOnly}
          >
            Add Award
          </Button>
        </>
      )}

      {/* Modal for Adding Entries */}
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="add-entry-modal-title"
        aria-describedby="add-entry-modal-description"
      >
        <Box sx={modalStyle}>
          <Typography
            id="add-entry-modal-title"
            variant="h6"
            component="h2"
            gutterBottom
          >
            Add {currentType}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormInput
                label={
                  currentType === "Course"
                    ? "Course Name"
                    : currentType === "Extracurricular"
                    ? "Activity"
                    : "Award Name"
                }
                id="Data1"
                value={newEntry.Data1}
                onChange={handleOldChange("Data1")}
                required
                disabled={isReadOnly}
              />
            </Grid>
            <Grid item xs={12}>
              <FormInput
                label={
                  currentType === "Course"
                    ? "Teacher/Sponsor"
                    : currentType === "Extracurricular"
                    ? "Teacher/Sponsor"
                    : "Organization"
                }
                id="Data2"
                value={newEntry.Data2}
                onChange={handleOldChange("Data2")}
                required
                disabled={isReadOnly}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sx={{ display: "flex", justifyContent: "flex-end" }}
            >
              <Button
                onClick={handleCloseModal}
                color="secondary"
                sx={{ mr: 2 }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveEntry}
                variant="contained"
                color="primary"
                disabled={isReadOnly}
              >
                Save
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>
    </div>
  );
}

export default memo(StepContent4);
